import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL;

const USER_ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'PATHOLOGIST', label: 'Pathologist' },
  { value: 'RECEPTIONIST', label: 'Receptionist' },
  { value: 'LABORATORIST', label: 'Laboratorist' }
];

const AdminPanel = () => {
  const [categories, setCategories] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Search states
  const [categorySearch, setCategorySearch] = useState('');
  const [testSearch, setTestSearch] = useState('');

  // Dialog states
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingTest, setEditingTest] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: ''
  });

  const [testForm, setTestForm] = useState({
    name: '',
    unit: '',
    referenceRange: '',
    dataType: 'NUMBER',
    testCategoryId: ''
  });

  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    education: '',
    designation: '',
    licenseNumber: ''
  });
  const [userRoleFilter, setUserRoleFilter] = useState('');

  // Validation for user form
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isUserFormValid = () => {
    return (
      userForm.name.trim() !== '' &&
      userForm.email.trim() !== '' &&
      isValidEmail(userForm.email) &&
      (editingUser || userForm.password.trim() !== '') &&
      userForm.role !== ''
    );
  };
  const [userEmailError, setUserEmailError] = useState('');

  // Filtered data
  const filteredCategories = categories
    .filter(category => 
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    )
    .slice(0, 10);

  const filteredTests = tests
    .filter(test => 
      test.name.toLowerCase().includes(testSearch.toLowerCase()) ||
      (test.testCategory?.name && test.testCategory.name.toLowerCase().includes(testSearch.toLowerCase()))
    )
    .slice(0, 10);

  useEffect(() => {
    fetchData();
    fetchUsers();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesResponse, testsResponse] = await Promise.all([
        axios.get(`${API_URL}/api/tests/categories`),
        axios.get(`${API_URL}/api/tests/all`)
      ]);
      
      setCategories(categoriesResponse.data);
      setTests(testsResponse.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (role) => {
    try {
      let response;
      if (role) {
        response = await axios.get(`${API_URL}/api/admin/users/role/${role}`);
      } else {
        response = await axios.get(`${API_URL}/api/admin/users`);
      }
      setUsers(response.data);
    } catch (err) {
      setError('Failed to load users');
    }
  };

  const handleCategorySubmit = async () => {
    try {
      const categoryData = { ...categoryForm };

      if (editingCategory) {
        await axios.put(`${API_URL}/api/admin/categories/${editingCategory.id}`, categoryData);
        setSuccess('Category updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/categories`, categoryData);
        setSuccess('Category created successfully');
      }
      
      setCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: ''});
      fetchData();
    } catch (err) {
      setError('Failed to save category');
    }
  };

  const handleTestSubmit = async () => {
    try {
      if (editingTest) {
        await axios.put(`${API_URL}/api/admin/tests/${editingTest.id}`, testForm);
        setSuccess('Test updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/tests`, testForm);
        setSuccess('Test created successfully');
      }
      
      setTestDialog(false);
      setEditingTest(null);
      setTestForm({
        name: '',
        unit: '',
        referenceRange: '',
        dataType: 'NUMBER',
        testCategoryId: ''
      });
      fetchData();
    } catch (err) {
      setError('Failed to save test');
    }
  };

  const handleUserSubmit = async () => {
    try {
      if (editingUser) {
        await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, userForm);
        setSuccess('User updated successfully');
      } else {
        await axios.post(`${API_URL}/api/admin/users`, userForm);
        setSuccess('User created successfully');
      }
      setUserDialog(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: '', education: '', designation: '', licenseNumber: '' });
      fetchUsers(userRoleFilter);
    } catch (err) {
      setError('Failed to save user');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/users/${id}`);
      setSuccess('User deleted successfully');
      fetchUsers(userRoleFilter);
    } catch (err) {
      setError('Failed to delete user');
    }
  };

  const handleUserRoleFilter = (role) => {
    setUserRoleFilter(role);
    fetchUsers(role);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 24px rgba(42,46,91,0.08)' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#2A2E5B', fontWeight: 700 }}>
          Admin Panel
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Categories Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Test Categories</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setCategoryDialog(true)}
                    size="small"
                    sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
                  >
                    Add
                  </Button>
                </Box>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: categorySearch && (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setCategorySearch('')}
                          sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                        >
                          Clear
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {categories.length} | Showing: {filteredCategories.length}
                  </Typography>
                  {categories.length > 10 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        (Latest 10 results)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setCategorySearch('')}
                        sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                      >
                        View All
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCategories.map((category) => (
                        <TableRow key={category.id} sx={{ background: (theme) => theme.palette.background.default }}>
                          <TableCell>{category.name}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => {
                              setEditingCategory(category);
                              setCategoryForm({ name: category.name || '' });
                              setCategoryDialog(true);
                            }}>
                              <Edit />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Tests Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Tests</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setTestDialog(true)}
                    size="small"
                    sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
                  >
                    Add
                  </Button>
                </Box>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search tests or categories..."
                  value={testSearch}
                  onChange={(e) => setTestSearch(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                    endAdornment: testSearch && (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={() => setTestSearch('')}
                          sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                        >
                          Clear
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Total: {tests.length} | Showing: {filteredTests.length}
                  </Typography>
                  {tests.length > 10 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        (Latest 10 results)
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setTestSearch('')}
                        sx={{ minWidth: 'auto', px: 1, py: 0.5 }}
                      >
                        View All
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredTests.map((test) => (
                        <TableRow key={test.id} sx={{ background: (theme) => theme.palette.background.default }}>
                          <TableCell>{test.name}</TableCell>
                          <TableCell>{test.testCategory?.name || 'N/A'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => {
                              setEditingTest(test);
                              setTestForm({
                                name: test.name,
                                unit: test.unit || '',
                                referenceRange: test.referenceRange || '',
                                dataType: test.dataType,
                                testCategoryId: test.testCategory?.id || ''
                              });
                              setTestDialog(true);
                            }}>
                              <Edit />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* User Management Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">User Management</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      setUserDialog(true);
                      setEditingUser(null);
                      setUserForm({ name: '', email: '', password: '', role: '', education: '', designation: '', licenseNumber: '' });
                    }}
                    size="small"
                    sx={{ background: 'linear-gradient(90deg, #2A2E5B 0%, #5F4B8B 100%)', borderRadius: '24px', fontWeight: 700, textTransform: 'none' }}
                  >
                    Add
                  </Button>
                </Box>
                <FormControl sx={{ minWidth: 200, mb: 2 }}>
                  <InputLabel>Filter by Role</InputLabel>
                  <Select
                    value={userRoleFilter}
                    label="Filter by Role"
                    onChange={(e) => handleUserRoleFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    {USER_ROLES.map((role) => (
                      <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Education</TableCell>
                        <TableCell>Designation</TableCell>
                        <TableCell>License Number</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id} sx={{ background: (theme) => theme.palette.background.default }}>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.role}</TableCell>
                          <TableCell>{user.education || '-'}</TableCell>
                          <TableCell>{user.designation || '-'}</TableCell>
                          <TableCell>{user.licenseNumber || '-'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => {
                              setEditingUser(user);
                              setUserForm({
                                name: user.name,
                                email: user.email,
                                password: '',
                                role: user.role,
                                education: user.education || '',
                                designation: user.designation || '',
                                licenseNumber: user.licenseNumber || ''
                              });
                              setUserDialog(true);
                            }}>
                              <Edit />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteUser(user.id)}>
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Category Dialog */}
        <Dialog open={categoryDialog} onClose={() => setCategoryDialog(false)}>
          <DialogTitle>{editingCategory ? 'Edit' : 'Add'} Category</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Category Name"
              type="text"
              fullWidth
              variant="standard"
              value={categoryForm.name}
              onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCategoryDialog(false);
              setEditingCategory(null);
            }}>Cancel</Button>
            <Button onClick={handleCategorySubmit}>Submit</Button>
          </DialogActions>
        </Dialog>

        {/* Test Dialog */}
        <Dialog open={testDialog} onClose={() => setTestDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTest ? 'Edit Test' : 'Add Test'}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={testForm.name}
              onChange={(e) => setTestForm({ ...testForm, name: e.target.value })}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Category</InputLabel>
              <Select
                value={testForm.testCategoryId}
                onChange={(e) => setTestForm({ ...testForm, testCategoryId: e.target.value })}
                label="Category"
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Unit"
              value={testForm.unit}
              onChange={(e) => setTestForm({ ...testForm, unit: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Reference Range"
              value={testForm.referenceRange}
              onChange={(e) => setTestForm({ ...testForm, referenceRange: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Data Type</InputLabel>
              <Select
                value={testForm.dataType}
                onChange={(e) => setTestForm({ ...testForm, dataType: e.target.value })}
                label="Data Type"
              >
                <MenuItem value="NUMBER">Number</MenuItem>
                <MenuItem value="TEXT">Text</MenuItem>
                <MenuItem value="BOOLEAN">Boolean</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestDialog(false)}>Cancel</Button>
            <Button onClick={handleTestSubmit} variant="contained">
              {editingTest ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* User Dialog */}
        <Dialog open={userDialog} onClose={() => setUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingUser ? 'Edit User' : 'Add User'}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Name"
              value={userForm.name}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              value={userForm.email}
              onChange={(e) => {
                setUserForm({ ...userForm, email: e.target.value });
                setUserEmailError(isValidEmail(e.target.value) ? '' : 'Enter a valid email address');
              }}
              margin="normal"
              required
              error={!!userEmailError}
              helperText={userEmailError}
            />
            {!editingUser && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                margin="normal"
                required
              />
            )}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Role</InputLabel>
              <Select
                value={userForm.role}
                label="Role"
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {(userForm.role === 'PATHOLOGIST' || userForm.role === 'LAB_INCHARGE') && (
              <>
                <TextField
                  fullWidth
                  label="Education"
                  value={userForm.education}
                  onChange={(e) => setUserForm({ ...userForm, education: e.target.value })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Designation"
                  value={userForm.designation}
                  onChange={(e) => setUserForm({ ...userForm, designation: e.target.value })}
                  margin="normal"
                />
              </>
            )}
            {userForm.role === 'PATHOLOGIST' && (
              <TextField
                fullWidth
                label="License Number"
                value={userForm.licenseNumber}
                onChange={(e) => setUserForm({ ...userForm, licenseNumber: e.target.value })}
                margin="normal"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setUserDialog(false);
              setEditingUser(null);
              setUserForm({ name: '', email: '', password: '', role: '', education: '', designation: '', licenseNumber: '' });
            }}>Cancel</Button>
            <Button onClick={handleUserSubmit} variant="contained" disabled={!isUserFormValid()}>
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default AdminPanel; 